import { action, computed, observable } from 'mobx';
import { computeMetricsSummaries, MetricsGranularity } from '../lib/metricsUtils';
import { ZipApi } from '../lib/ZipApi';
import { DataSyncMetricsBucket, DataSyncMetricsSummary } from '../types/DataSyncMetrics';

export class DataSyncMetricsStore {
  @observable public loading = false;
  @observable private _summaries = new Map<string, DataSyncMetricsSummary>();
  @observable private _bucketsByDataSync = new Map<string, DataSyncMetricsBucket[]>();
  private _loadedKeys = new Set<string>();

  @computed
  public get summaries(): Map<string, DataSyncMetricsSummary> {
    return this._summaries;
  }

  @computed
  public get bucketsByDataSync(): Map<string, DataSyncMetricsBucket[]> {
    return this._bucketsByDataSync;
  }

  @action
  public clear() {
    this._summaries = new Map();
    this._bucketsByDataSync = new Map();
    this._loadedKeys = new Set();
  }

  @action
  public async fetchMetrics(
    trackerId: string,
    datasyncIds: string[],
    options?: {
      fromEpoch?: number;
      toEpoch?: number;
      granularity?: MetricsGranularity;
    }
  ) {
    if (!trackerId || datasyncIds.length === 0) {
      return;
    }

    const toEpoch = options?.toEpoch || Math.floor(Date.now() / 1000);
    const fromEpoch = options?.fromEpoch || (toEpoch - 7 * 24 * 60 * 60);
    const granularity = options?.granularity || 'METRICS_GRANULARITY_DAILY';

    const keyFor = (datasyncId: string) => `${trackerId}:${fromEpoch}:${toEpoch}:${granularity}:${datasyncId}`;
    const idsToFetch = datasyncIds.filter((datasyncId) => !this._loadedKeys.has(keyFor(datasyncId)));

    if (idsToFetch.length === 0) {
      return;
    }

    this.loading = true;
    try {
      const response = await ZipApi.getDataSyncMetrics(
        trackerId,
        idsToFetch,
        fromEpoch,
        toEpoch,
        granularity
      );
      const buckets = response.metrics_buckets || [];

      const groupedBuckets = new Map<string, DataSyncMetricsBucket[]>();
      idsToFetch.forEach((datasyncId) => groupedBuckets.set(datasyncId, []));
      buckets.forEach((bucket) => {
        const entries = groupedBuckets.get(bucket.datasync_id) || [];
        entries.push(bucket);
        groupedBuckets.set(bucket.datasync_id, entries);
      });

      groupedBuckets.forEach((entries, datasyncId) => {
        this._bucketsByDataSync.set(datasyncId, entries);
      });

      const newSummaries = computeMetricsSummaries(buckets, {
        fromEpoch,
        toEpoch,
        granularity,
      });
      newSummaries.forEach((summary, datasyncId) => {
        this._summaries.set(datasyncId, summary);
      });

      idsToFetch.forEach((datasyncId) => {
        this._loadedKeys.add(keyFor(datasyncId));
      });
    } finally {
      this.loading = false;
    }
  }

  public getSummary(datasyncId: string): DataSyncMetricsSummary | undefined {
    return this._summaries.get(datasyncId);
  }

  public getBuckets(datasyncId: string): DataSyncMetricsBucket[] {
    return this._bucketsByDataSync.get(datasyncId) || [];
  }
}

export const dataSyncMetricsStore = new DataSyncMetricsStore();
